import { createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { handleError, isEmpty } from "../../utils/basic";
import {
  computeComponentVersion,
  getComponentInput,
  getFileInput,
} from "../../components/Component/helpers";
import get from "lodash/get";
import set from "lodash/set";
import { v4 as uuid } from "uuid";
import { removeFileToS3 } from "../../utils/StorageManager";
import { addRelationship, createEntity } from "../../data/delicDataClient";
import {
  getFolderPackagingJob,
  startFolderPackaging,
} from "../../data/folderPackagingClient";
import { fetchTasksInProject, updateTask } from "./tasksSlice";
import { TaskStatus } from "../../components/Tasks/TaskStatus";
import {
  addInput,
  getPayloads,
  getSelectableVersionsOptions,
  getLatestVersionsOfComponents,
  getComponentsInSelectedProjectByGroupAndOrdered,
  getSameSpaceGroupedComponents,
  getLocation,
  createNewIdeaWithComment,
  getComponentGroupAndWeights,
  getFirstShareableComponent,
  getUploadedFileUris,
  getUploadPrimaryUri,
  getUploadCorrelationKey,
  buildGroupedComponentsFromTransferResults,
  getReferencesFromTransferResults,
} from "./transferManagerSlice.helpers";
import {
  getReferenceType,
  linkCleaner,
} from "../../components/References/referenceParser";
import { normalizeComponents } from "./componentsSlice";
import { getlatestVersionsByProject } from "../../components/Component/helpers";
import { readEntity } from "../../data/delicDataClient";
import { LOCATION_CHANGE, push } from "connected-react-router";
import {
  getFileCategoryType,
  isVersionUploadTypeCompatible,
  RECORDING,
  VIDEO,
} from "../../utils/fileUtils";
import { getUploadManager } from "../../uploadTransfer/uploadManagerSingleton";
import {
  registerUpload,
  selectUploadById,
  updateUpload,
} from "../../uploadTransfer/uploadSlice";
import { requestPersistentUploadStorage } from "../../uploadTransfer/uploadPersistence";
import { runUploadBatch } from "../../uploadTransfer/uploadScheduler";

export const transferManagerAdapter = createEntityAdapter();

const UPLOAD_INTERRUPTED_MESSAGE =
  "Upload was interrupted. Check your connection and retry.";
export const INCOMPATIBLE_VERSION_UPLOAD_MESSAGE =
  "This file type cannot be uploaded as a version of the selected file. No new file was created.";

const uploadFileCache = new Map();
const folderPackageFileCache = new Map();
const folderUploadFileCache = new Map();

export const FOLDER_PACKAGING_STATUS = {
  READY_TO_UPLOAD_RAW_FILES: "READY_TO_UPLOAD_RAW_FILES",
  UPLOADING_RAW_FILES: "UPLOADING_RAW_FILES",
  UPLOADING_MANIFEST: "UPLOADING_MANIFEST",
  CREATING_PACKAGING_JOB: "CREATING_PACKAGING_JOB",
  PACKAGING_PENDING: "PACKAGING_PENDING",
  PACKAGING_READY: "PACKAGING_READY",
  PACKAGING_FAILED: "PACKAGING_FAILED",
};

const FOLDER_PACKAGING_POLL_INTERVAL_MS = 5000;
const FOLDER_PACKAGING_MAX_POLL_ATTEMPTS = 120;
const FOLDER_PACKAGE_DESCRIPTOR_VERSION = 1;
const FOLDER_PACKAGE_DESCRIPTOR_CONTENT_TYPE =
  "application/vnd.streamline.folder-package+json";
const MAX_GRAPHQL_INT = 2147483647;

function isActiveTransferWork(uploadedFile) {
  if (get(uploadedFile, "isUploaded", false)) return false;
  if (get(uploadedFile, "isUploadFailed", false)) {
    return !get(uploadedFile, "retryUnavailable", false);
  }
  return true;
}

function hasActiveTransferWork(uploadedFiles = []) {
  return uploadedFiles.some(isActiveTransferWork);
}

function isDestinationLocked(state) {
  return Boolean(state.activeUploadDestinationProjectId);
}

function hasTransferContents(state) {
  return (
    !isEmpty(state.uploadedFiles) ||
    !isEmpty(state.importedComponents) ||
    Boolean(state.idea)
  );
}

function syncActiveUploadDestinationLock(state) {
  if (hasActiveTransferWork(state.uploadedFiles)) {
    const lockedProjectId =
      state.activeUploadDestinationProjectId ||
      state.selectedProjectId ||
      "Dashboard";
    state.activeUploadDestinationProjectId = lockedProjectId;
    state.selectedProjectId = lockedProjectId;
    return;
  }

  state.activeUploadDestinationProjectId = "";
}

function getUploadFileCacheKey(uploadedFile) {
  return get(uploadedFile, "idForComparison", "");
}

function cacheUploadFile(uploadedFile, file) {
  const cacheKey = getUploadFileCacheKey(uploadedFile);
  if (cacheKey && file) {
    uploadFileCache.set(cacheKey, file);
  }
}

function getCachedUploadFile(uploadedFile) {
  return uploadFileCache.get(getUploadFileCacheKey(uploadedFile));
}

export function forgetCachedUploadFile(idForComparison) {
  if (idForComparison) {
    uploadFileCache.delete(idForComparison);
    folderPackageFileCache.delete(idForComparison);
    folderUploadFileCache.delete(idForComparison);
  }
}

function cacheFolderPackageFiles(uploadedFile, rawFiles) {
  const cacheKey = getUploadFileCacheKey(uploadedFile);
  if (cacheKey && rawFiles) {
    folderPackageFileCache.set(cacheKey, rawFiles);
  }
}

function getCachedFolderPackageFiles(uploadedFile) {
  return folderPackageFileCache.get(getUploadFileCacheKey(uploadedFile));
}

function cacheFolderUploadFiles(uploadedFile, childFiles) {
  const cacheKey = getUploadFileCacheKey(uploadedFile);
  if (cacheKey && childFiles) {
    folderUploadFileCache.set(cacheKey, childFiles);
  }
}

function getCachedFolderUploadFiles(uploadedFile) {
  return folderUploadFileCache.get(getUploadFileCacheKey(uploadedFile));
}

function clearCachedUploadFiles(uploadedFiles = []) {
  uploadedFiles.forEach((uploadedFile) =>
    forgetCachedUploadFile(getUploadFileCacheKey(uploadedFile))
  );
}

function getUploadTotalBytes(uploadedFile) {
  return get(
    uploadedFile,
    "uploadTotalBytes",
    get(uploadedFile, "payload.fileInput.size", 0)
  );
}

function getUploadedFileById(state, idForComparison) {
  return state.uploadedFiles.find(
    (uploadedFile) => uploadedFile.idForComparison === idForComparison
  );
}

function isUploadedFileReadyForTransfer(uploadedFile) {
  return (
    uploadedFile.isUploaded &&
    Boolean(getUploadPrimaryUri(uploadedFile))
  );
}

function isStaleUploadAttempt(uploadedFile, uploadAttemptId) {
  return (
    uploadAttemptId &&
    get(uploadedFile, "uploadAttemptId", "") !== uploadAttemptId
  );
}

function getUploadErrorMessage(error) {
  const message = (
    typeof error === "string" ? error : get(error, "message", "")
  ).toLowerCase();

  if (
    message.includes("network") ||
    message.includes("offline") ||
    message.includes("failed to fetch")
  ) {
    return UPLOAD_INTERRUPTED_MESSAGE;
  }

  if (
    message.includes("accessdenied") ||
    message.includes("forbidden") ||
    message.includes("403") ||
    message.includes("credential")
  ) {
    return "Upload was not authorised. Please sign out and back in, then try again.";
  }

  if (
    message.includes("timeout") ||
    message.includes("abort") ||
    message.includes("cancel")
  ) {
    return "Upload stopped before it finished. Retry when your connection is stable.";
  }

  if (
    message.includes("entitytoolarge") ||
    message.includes("too large") ||
    message.includes("413")
  ) {
    return "This file is too large for the current upload path.";
  }

  return "Upload failed. Please retry.";
}

function getSafeGraphqlInt(value) {
  const numberValue = Number(value) || 0;
  return numberValue <= MAX_GRAPHQL_INT ? numberValue : null;
}

function updateOverallUploadProgress(state) {
  const uploadedFiles = state.uploadedFiles || [];
  const totalBytes = uploadedFiles.reduce(
    (total, uploadedFile) => total + (getUploadTotalBytes(uploadedFile) || 0),
    0
  );

  if (totalBytes > 0) {
    const uploadedBytes = uploadedFiles.reduce((total, uploadedFile) => {
      const fileTotalBytes = getUploadTotalBytes(uploadedFile) || 0;
      if (uploadedFile.isUploaded) {
        return total + fileTotalBytes;
      }
      return (
        total + Math.min(uploadedFile.uploadLoadedBytes || 0, fileTotalBytes)
      );
    }, 0);
    state.uploadProgress = Math.min(100, (uploadedBytes / totalBytes) * 100);
  } else {
    const completedFiles = uploadedFiles.filter(
      (uploadedFile) => uploadedFile.isUploaded
    ).length;
    state.uploadProgress = uploadedFiles.length
      ? (completedFiles / uploadedFiles.length) * 100
      : 0;
  }

  state.isUploading = uploadedFiles.some(
    (uploadedFile) => uploadedFile.isUploading
  );
}

const initialState = transferManagerAdapter.getInitialState({
  isLoading: true,
  isTransfering: false,
  isOpen: false,
  canImport: true,
  error: false,
  targetParentComponent: null,
  targetLatestVersionComponent: null,
  isNewProject: false,
  newProjectName: "",
  selectedProjectId: "Dashboard",
  activeUploadDestinationProjectId: "",
  selectedTaskId: "",
  uploadedFiles: [],
  importedComponents: [],
  selectedComponentIdToShare: null,
  sharedComponentUri: "",
  projectInvitationPayload: null,
  isUploading: false,
  uploadProgress: 0,
  progressStatus: 0,
  currentUploadedFile: null,
  allProjectsComponents: [],
  componentsNotInCurrentProject: null,
  selectableVersionsOptions: [],
  isLoadingComponentForProject: null,
  idea: null,
  isLoadingAllComponentsUploadedByUser: false,
  errorGettingAllComponentsUploadedByUser: null,
});

const transferManager = createSlice({
  name: "transferManager",
  initialState,
  reducers: {
    onLoaded(state) {
      state.isLoading = false;
    },
    openPanel(state) {
      syncActiveUploadDestinationLock(state);
      if (!isDestinationLocked(state) && !hasTransferContents(state)) {
        state.selectedProjectId = state.location.project
          ? state.location.project
          : "Dashboard";
      }
      state.isOpen = true;
    },
    openPanelForNewVersion(state, { payload }) {
      const { parentComponent, selectedVersion, selectedProjectId } = payload;
      syncActiveUploadDestinationLock(state);
      if (!isDestinationLocked(state) && !hasTransferContents(state)) {
        state.selectedProjectId = state.location.project
          ? state.location.project
          : "Dashboard";
      }
      state.isOpen = true;
      state.targetParentComponent = parentComponent;
      state.targetLatestVersionComponent = selectedVersion;
      if (!isDestinationLocked(state)) {
        state.selectedProjectId = selectedProjectId;
      }
    },
    setAllProjectsGroupedComponents(state, { payload }) {
      const { allProjectsGroupedComponents } = payload;

      state.allProjectsGroupedComponents = allProjectsGroupedComponents;
    },
    addUploadsFiles(state, { payload }) {
      const { uploadedFiles } = payload;

      state.uploadedFiles = [...state.uploadedFiles, ...uploadedFiles];
      syncActiveUploadDestinationLock(state);
    },
    setUploadedComponentIsIdeaOrNot(state, { payload }) {
      const {
        selectedComponentIdToShare,
        uploadedFiles,
        importedComponents,
      } = state;
      const { position, isIdea } = payload;
      const targetFile = state.uploadedFiles[position];
      set(targetFile, "isIdea", isIdea);

      if (
        !selectedComponentIdToShare ||
        (isIdea &&
          get(targetFile, "payload.componentInput.id") ===
            selectedComponentIdToShare)
      ) {
        const newShareableComponent = getFirstShareableComponent(
          uploadedFiles,
          importedComponents
        );
        state.selectedComponentIdToShare = newShareableComponent;
      }
    },
    renameUploadedComponent(state, { payload }) {
      const { position, newTitle } = payload;
      const targetFile = state.uploadedFiles[position];
      set(
        targetFile,
        "previousTitle",
        get(targetFile, "payload.componentInput.title", "")
      );
      set(targetFile, "payload.componentInput.title", newTitle);
    },
    setStartedUploadingComponent(state, { payload }) {
      const { idForComparison, uploadAttemptId } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (!targetFile) return;
      set(targetFile, "isUploading", true);
      set(targetFile, "isUploadFailed", false);
      set(targetFile, "uploadError", "");
      set(targetFile, "uploadLoadedBytes", 0);
      set(targetFile, "uploadProgress", 0);
      set(targetFile, "retryUnavailable", false);
      set(targetFile, "uploadAttemptId", uploadAttemptId);
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    removeUploadedComponent(state, { payload }) {
      const { position, idForComparison } = payload;
      const uploadedFiles = state.uploadedFiles;
      const targetPosition = idForComparison
        ? uploadedFiles.findIndex(
            (uploadedFile) => uploadedFile.idForComparison === idForComparison
          )
        : position;
      if (targetPosition < 0) return;
      state.uploadedFiles = [
        ...uploadedFiles.slice(0, targetPosition),
        ...uploadedFiles.slice(targetPosition + 1),
      ];
      const shareableComponentStillExists = state.uploadedFiles
        .concat(state.importedComponents)
        .some(
          (file) =>
            get(file, "payload.componentInput.id") ===
            state.selectedComponentIdToShare
      );
      if (!shareableComponentStillExists) {
        state.selectedComponentIdToShare =
          getFirstShareableComponent(
            state.uploadedFiles,
            state.importedComponents
          ) || null;
      }
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    addImportedComponents(state, { payload }) {
      const { selectedProjectId, uploadedFiles, importedComponents } = state;
      const {
        personId,
        accountId,
        parentComponents,
        selectedComponentIdToShare,
      } = payload;

      const importedFiles = [];

      parentComponents.forEach((parentComponent) => {
        const isAlreadyAdded = state.importedComponents.find(
          (impFile) => impFile.payload.parentComponentId === parentComponent.id
        );
        if (isAlreadyAdded) return;

        const workId = get(parentComponent, "work[0].id", "");

        const fileInput = getFileInput(get(parentComponent, "file[0]"));
        const payload = {
          partyId: personId,
          componentInput: getComponentInput(
            parentComponent.title,
            computeComponentVersion(parentComponent.version).getNewNested,
            parentComponent.software
          ),
          fileInput,
          componentGroup: get(
            parentComponent,
            "groupInProject.componentGroup",
            "Default"
          ),
          componentGroupOrderWeight: 0,
          componentOrderWeight: 0,
          parentComponentId: parentComponent.id,
          latestVersionComponentId: parentComponent.id,
          workId,
          fullFileKey: "protected/" + accountId + "/" + fileInput.uri,
        };

        let uploaderId = "";
        const uploaderContr = get(parentComponent, "isContributionOf", []).find(
          (contr) => contr.role === "uploader"
        );
        if (uploaderContr) {
          uploaderId = get(uploaderContr, "contributor[0].accountId", "");
        }

        const fileCategoryType = getFileCategoryType(
          fileInput.mimeType,
          fileInput.fileType
        );

        const preparedFile = {
          payload,
          isVersion: true,
          sourceFile: parentComponent["__typename"] === "SourceFile",
          type: parentComponent["__typename"],
          idForComparison: uuid(),
          isUploaded: true,
          uploaderId: uploaderId,
          isIdea: false,
          parentComponent,
          label: fileCategoryType,
          isWorkRequired:
            fileCategoryType === RECORDING || fileCategoryType === VIDEO,
        };

        importedFiles.push(preparedFile);
      });

      importedFiles.forEach((item, position) => {
        const itemProjectId = get(item, "parentComponent.project[0].id", "");
        if (itemProjectId === selectedProjectId) {
          removeImportedComponent(state, { position });
        }
        if (item.isIdea) {
          setImportedComponentIsIdeaOrNot(state, { position, isIdea: false });
        }
      });

      if (!selectedComponentIdToShare) {
        const newShareableComponent = getFirstShareableComponent(
          uploadedFiles,
          importedComponents
        );
        if (newShareableComponent) {
          state.selectedComponentIdToShare = newShareableComponent;
        }
      }

      // default to ideas if in ideas panels
      if (state.location.ideas) {
        const ideas = importedFiles.map((file) => {
          file.isIdea = true;
          return file;
        });
        state.importedComponents = state.importedComponents.concat(ideas);
      } else {
        state.importedComponents = state.importedComponents.concat(
          importedFiles
        );
      }
    },
    setImportedComponentIsIdeaOrNot(state, { payload }) {
      const {
        selectedComponentIdToShare,
        uploadedFiles,
        importedComponents,
      } = state;
      const { position, isIdea } = payload;
      const targetFile = state.importedComponents[position];
      set(targetFile, "isIdea", isIdea);

      if (
        !selectedComponentIdToShare ||
        (isIdea &&
          get(targetFile, "payload.componentInput.id") ===
            selectedComponentIdToShare)
      ) {
        const newShareableComponent = getFirstShareableComponent(
          uploadedFiles,
          importedComponents
        );
        state.selectedComponentIdToShare = newShareableComponent;
      }
    },
    renameImportedComponent(state, { payload }) {
      const { position, newTitle } = payload;
      const targetFile = state.importedComponents[position];
      set(
        targetFile,
        "previousTitle",
        get(targetFile, "payload.componentInput.title", "")
      );
      set(targetFile, "payload.componentInput.title", newTitle);
    },
    removeImportedComponent(state, { payload }) {
      const { position } = payload;
      const importedComponents = state.importedComponents;
      state.importedComponents = [
        ...importedComponents.slice(0, position),
        ...importedComponents.slice(position + 1),
      ];
    },
    setComponentVersion(state, { payload }) {
      const { position, isUploadType, option } = payload;

      const { allProjectsGroupedComponents } = state;

      const file = isUploadType ? state.uploadedFiles[position] : [];
      if (option !== "" && allProjectsGroupedComponents) {
        let parentComponent = allProjectsGroupedComponents
          .filter(
            (comp) =>
              comp.versions && comp.versions.find((c) => c.id === option)
          )
          .pop();
        if (!parentComponent) {
          parentComponent = allProjectsGroupedComponents.find(
            (comp) => comp.id === option
          );
        }
        if (parentComponent) {
          const latestVersion =
            parentComponent.versions.find((c) => c.id === option) ||
            parentComponent;

          file.payload.parentComponentId = get(parentComponent, "id", "");
          file.payload.workId = get(parentComponent, "work[0].id", "");
          file.payload.latestVersionComponentId = option;
          file.payload.componentInput.version = computeComponentVersion(
            latestVersion.version
          ).increase;
          file.previousTitle = file.payload.componentInput.title;
          file.payload.componentInput.title = get(parentComponent, "title", "");
          file.isVersion = true;
        } else {
          throw new Error("No parent component when setting version");
        }
      } else {
        file.payload.parentComponentId = "";
        file.payload.latestVersionComponentId = "";
        file.isVersion = false;
        file.payload.componentInput.version = "1";
        file.payload.componentInput.title = file.previousTitle
          ? file.previousTitle
          : file.payload.componentInput.title;
        file.payload.componentGroup = file.previousGroup
          ? file.previousGroup
          : file.payload.componentGroup;
        file.payload.componentGroupOrderWeight = 0;
        file.payload.componentOrderWeight = 0;
      }
    },
    completeUploadForUploadedFile(state, { payload }) {
      const {
        accountId,
        idForComparison,
        uploadAttemptId,
        componentId,
        fileKey,
        fileInputs,
      } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (!targetFile) return;
      if (isStaleUploadAttempt(targetFile, uploadAttemptId)) return;
      if (!targetFile.isUploading) return;
      set(targetFile, "payload.componentInput.id", componentId);
      if (fileInputs && fileInputs.length) {
        set(targetFile, "payload.fileInputs", fileInputs);
      } else {
        set(targetFile, "payload.fileInput.uri", fileKey);
      }
      set(
        targetFile,
        "payload.fullFileKey",
        "protected/" + accountId + "/" + (fileKey || get(fileInputs, "[0].uri", ""))
      );
      set(targetFile, "isUploaded", true);
      set(targetFile, "isUploading", false);
      set(targetFile, "isUploadFailed", false);
      set(targetFile, "uploadError", "");
      set(targetFile, "uploadLoadedBytes", getUploadTotalBytes(targetFile));
      set(targetFile, "uploadProgress", 100);
      set(targetFile, "retryUnavailable", false);
      set(targetFile, "uploadAttemptId", "");
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    completeFolderPackageCommitForUploadedFile(state, { payload }) {
      const {
        accountId,
        descriptorKey,
        idForComparison,
        manifestKey,
        rawPrefix,
        uploadAttemptId,
        job,
      } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (!targetFile) return;
      if (isStaleUploadAttempt(targetFile, uploadAttemptId)) return;
      if (!targetFile.isFolderPackage) return;

      const metadata = getFolderPackageMetadata({
        job,
        manifestKey,
        rawPrefix,
        descriptorKey,
        status: get(job, "status", "PENDING"),
      });

      set(
        targetFile,
        "folderPackagingStatus",
        FOLDER_PACKAGING_STATUS.PACKAGING_PENDING
      );
      set(targetFile, "folderPackagingJob", {
        ...targetFile.folderPackagingJob,
        ...job,
      });
      set(targetFile, "payload.componentInput.id", get(job, "jobId", ""));
      set(targetFile, "payload.fileInput.uri", descriptorKey);
      set(targetFile, "payload.fileInput", {
        ...targetFile.payload.fileInput,
        ...metadata,
        uri: descriptorKey,
      });
      set(
        targetFile,
        "payload.fullFileKey",
        buildProtectedFolderPackagingKey(accountId, descriptorKey)
      );
      set(targetFile, "isUploaded", true);
      set(targetFile, "isUploading", false);
      set(targetFile, "isUploadFailed", false);
      set(targetFile, "uploadError", "");
      set(targetFile, "uploadLoadedBytes", getUploadTotalBytes(targetFile));
      set(targetFile, "uploadProgress", 100);
      set(targetFile, "retryUnavailable", false);
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    completeFolderPackagingForUploadedFile(state, { payload }) {
      const { accountId, idForComparison, uploadAttemptId, job } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (!targetFile) return;
      if (isStaleUploadAttempt(targetFile, uploadAttemptId)) return;
      if (!targetFile.isFolderPackage) return;

      const fileKey = getFolderPackagingAppFileKey(job, accountId);
      if (!fileKey) return;

      const outputSize = Number(get(job, "outputSize", 0));
      const componentId =
        get(job, "componentId", "") ||
        getComponentIdFromFileKey(fileKey) ||
        get(job, "jobId", "");

      set(
        targetFile,
        "folderPackagingStatus",
        FOLDER_PACKAGING_STATUS.PACKAGING_READY
      );
      set(targetFile, "folderPackagingJob", {
        ...targetFile.folderPackagingJob,
        ...job,
      });
      set(targetFile, "payload.componentInput.id", componentId);
      set(targetFile, "payload.fileInput", {
        ...targetFile.payload.fileInput,
        ...getFolderPackageMetadata({
          job,
          manifestKey: get(
            targetFile,
            "payload.fileInput.folderPackageManifestKey",
            ""
          ),
          rawPrefix: get(
            targetFile,
            "payload.fileInput.folderPackageRawPrefix",
            ""
          ),
          descriptorKey: get(
            targetFile,
            "payload.fileInput.folderPackageDescriptorKey",
            ""
          ),
          status: "READY",
        }),
      });
      if (!get(targetFile, "payload.fileInput.uri", "")) {
        set(targetFile, "payload.fileInput.uri", fileKey);
        set(
          targetFile,
          "payload.fullFileKey",
          get(job, "fullFinalFileKey", "") ||
            buildProtectedFolderPackagingKey(accountId, fileKey)
        );
      }
      if (outputSize > 0) {
        set(
          targetFile,
          "payload.fileInput.size",
          getSafeGraphqlInt(outputSize)
        );
        set(targetFile, "fileInput.size", getSafeGraphqlInt(outputSize));
        set(targetFile, "uploadTotalBytes", outputSize);
      }
      set(targetFile, "isUploaded", true);
      set(targetFile, "isUploading", false);
      set(targetFile, "isUploadFailed", false);
      set(targetFile, "uploadError", "");
      set(targetFile, "uploadLoadedBytes", getUploadTotalBytes(targetFile));
      set(targetFile, "uploadProgress", 100);
      set(targetFile, "retryUnavailable", false);
      set(targetFile, "uploadAttemptId", "");
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    failUploadForUploadedFile(state, { payload }) {
      const {
        idForComparison,
        uploadAttemptId,
        error,
        retryUnavailable = false,
      } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (!targetFile) return;
      if (isStaleUploadAttempt(targetFile, uploadAttemptId)) return;
      if (targetFile.isUploaded) return;
      set(targetFile, "isUploaded", false);
      set(targetFile, "isUploading", false);
      set(targetFile, "isUploadFailed", true);
      if (targetFile.uploadJobId) {
        set(targetFile, "uploadControlStatus", "failed");
      }
      set(targetFile, "uploadError", error);
      set(targetFile, "uploadLoadedBytes", 0);
      set(targetFile, "uploadProgress", 0);
      set(targetFile, "retryUnavailable", retryUnavailable);
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    setFolderPackagingStatus(state, { payload }) {
      const {
        idForComparison,
        uploadAttemptId,
        status,
        job,
        error,
        retryUnavailable = false,
      } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (!targetFile) return;
      if (isStaleUploadAttempt(targetFile, uploadAttemptId)) return;
      if (!targetFile.isFolderPackage) return;
      set(targetFile, "folderPackagingStatus", status);
      if (job) {
        set(targetFile, "folderPackagingJob", {
          ...targetFile.folderPackagingJob,
          ...job,
        });
        set(targetFile, "payload.fileInput", {
          ...targetFile.payload.fileInput,
          ...getFolderPackageMetadata({
            job,
            manifestKey: get(
              targetFile,
              "payload.fileInput.folderPackageManifestKey",
              ""
            ),
            rawPrefix: get(
              targetFile,
              "payload.fileInput.folderPackageRawPrefix",
              ""
            ),
            descriptorKey: get(
              targetFile,
              "payload.fileInput.folderPackageDescriptorKey",
              ""
            ),
            status: get(job, "status", status),
          }),
        });
      }
      if (status === FOLDER_PACKAGING_STATUS.PACKAGING_FAILED) {
        set(targetFile, "isUploading", false);
        set(
          targetFile,
          "isUploadFailed",
          !(
            targetFile.isUploaded &&
            get(targetFile, "payload.fileInput.uri", "")
          )
        );
        set(targetFile, "uploadError", error || "Folder packaging failed. Please retry.");
        set(targetFile, "retryUnavailable", retryUnavailable);
      } else {
        set(targetFile, "uploadError", "");
        set(targetFile, "isUploadFailed", false);
        set(targetFile, "retryUnavailable", false);
        set(
          targetFile,
          "isUploading",
          status === FOLDER_PACKAGING_STATUS.UPLOADING_RAW_FILES ||
            status === FOLDER_PACKAGING_STATUS.UPLOADING_MANIFEST ||
            status === FOLDER_PACKAGING_STATUS.CREATING_PACKAGING_JOB
        );
      }
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    setSelectedProject(state, { payload }) {
      syncActiveUploadDestinationLock(state);
      if (isDestinationLocked(state)) return;

      const {
        selectedComponentIdToShare,
        uploadedFiles,
        importedComponents,
      } = state;
      const { newProjectId } = payload;

      state.selectedProjectId = newProjectId;
      state.isNewProject = false;
      state.newProjectName = "";

      if (!selectedComponentIdToShare && newProjectId !== "Dashboard") {
        const newShareableComponent = getFirstShareableComponent(
          uploadedFiles,
          importedComponents
        );
        if (newShareableComponent) {
          state.selectedComponentIdToShare = newShareableComponent;
        }
      }
    },
    removeIdeasFromDashboard(state) {
      const { importedComponents, uploadedFiles, selectedProjectId } = state;
      // Users can not have add work ideas in dashboard
      if (selectedProjectId === "Dashboard") {
        if (importedComponents.length) {
          state.importedComponents = importedComponents.map((c) => {
            c.isIdea = false;
            return c;
          });
        }
        if (uploadedFiles.length)
          state.uploadedFiles = uploadedFiles.map((c) => {
            c.isIdea = false;
            return c;
          });
      }
    },
    setSelectableVersionsOptions(state, { payload }) {
      state.selectableVersionsOptions = payload.selectableVersionsOptions;
    },
    resetVersionsIfFromDifferentProject(state) {
      const { selectedProjectId } = state;
      const uploadedComponents = state.uploadedFiles;
      for (let i = 0; i < uploadedComponents.length; i++) {
        const uploadedComp = uploadedComponents[i];
        // users can not upload versions of files in projects that don't have that file
        const versionOfId = get(
          uploadedComp,
          "payload.latestVersionComponentId",
          ""
        );
        if (versionOfId) {
          const versionOfComponent = state.selectableVersionsOptions.find(
            (opts) => opts.value === versionOfId
          );
          if (
            !versionOfComponent ||
            (versionOfComponent &&
              versionOfComponent.projectId !== selectedProjectId)
          ) {
            uploadedComp.payload.parentComponentId = "";
            uploadedComp.payload.latestVersionComponentId = "";
            uploadedComp.isVersion = false;
            uploadedComp.payload.componentInput.version = "1";
            uploadedComp.payload.componentInput.title = uploadedComp.previousTitle
              ? uploadedComp.previousTitle
              : uploadedComp.payload.componentInput.title;
            uploadedComp.payload.componentGroup = uploadedComp.previousGroup
              ? uploadedComp.previousGroup
              : uploadedComp.payload.componentGroup;
            uploadedComp.payload.componentGroupOrderWeight = 0;
            uploadedComp.payload.componentOrderWeight = 0;
          }
        }
      }
    },
    createProject(state, { payload }) {
      syncActiveUploadDestinationLock(state);
      if (isDestinationLocked(state)) return;

      const { projectName } = payload;

      const {
        selectedComponentIdToShare,
        uploadedFiles,
        importedComponents,
      } = state;

      state.isNewProject = true;
      state.newProjectName = projectName;
      state.selectedProjectId = uuid();

      state.selectableVersionsOptions = [];

      if (!selectedComponentIdToShare) {
        const newShareableComponent = getFirstShareableComponent(
          uploadedFiles,
          importedComponents
        );
        if (newShareableComponent) {
          state.selectedComponentIdToShare = newShareableComponent;
        }
      }

      uploadedFiles.forEach((uploadedComp) => {
        // users can not upload versions of files in projects that don't have that file
        const versionOfId = get(
          uploadedComp,
          "payload.latestVersionComponentId",
          ""
        );
        if (versionOfId) {
          uploadedComp.payload.parentComponentId = "";
          uploadedComp.payload.latestVersionComponentId = "";
          uploadedComp.isVersion = false;
          uploadedComp.payload.componentInput.version = 1;
          uploadedComp.payload.componentInput.title = uploadedComp.previousTitle
            ? uploadedComp.previousTitle
            : uploadedComp.payload.componentInput.title;
          uploadedComp.payload.componentGroup = uploadedComp.previousGroup
            ? uploadedComp.previousGroup
            : uploadedComp.payload.componentGroup;
          uploadedComp.payload.componentGroupOrderWeight = 0;
          uploadedComp.payload.componentOrderWeight = 0;
        }
      });
    },
    setSelectedComponentToShare(state, { payload }) {
      let componentId;
      if (payload) {
        componentId = payload.componentId;
      } else {
        const { uploadedFiles, importedComponents } = state;
        componentId = getFirstShareableComponent(
          uploadedFiles,
          importedComponents
        );
      }

      state.selectedComponentIdToShare = componentId;
    },
    shareComponent(state, { payload }) {
      const { sharedComponentUri } = payload;
      state.sharedComponentUri = sharedComponentUri;
    },
    stopShareComponent(state) {
      state.selectedComponentIdToShare = null;
      state.sharedComponentUri = "";
    },
    inviteToProject(state, { payload }) {
      state.projectInvitationPayload = payload;
    },
    stopInviteToProject(state) {
      state.projectInvitationPayload = null;
    },
    selectTask(state, { payload }) {
      syncActiveUploadDestinationLock(state);
      if (isDestinationLocked(state)) return;

      const { taskId } = payload;
      state.selectedTaskId = taskId;
    },
    hidePanel(state) {
      state.isOpen = false;
      if (isEmpty(state.uploadedFiles) && isEmpty(state.importedComponents)) {
        state.targetParentComponent = null;
        state.targetLatestVersionComponent = null;
      }
    },
    closePanel(state) {
      const location = state.location;
      return { ...initialState, location };
    },
    handleGeneralUploadProgression(state, { payload }) {
      const { idForComparison, uploadAttemptId, loaded, total } = payload;
      const targetFile = getUploadedFileById(state, idForComparison);
      if (targetFile) {
        if (isStaleUploadAttempt(targetFile, uploadAttemptId)) return;
        if (!targetFile.isUploading) return;
        if (!state.isUploading) {
          state.isUploading = true;
        }
        const safeTotal = total || getUploadTotalBytes(targetFile);
        set(targetFile, "uploadLoadedBytes", loaded || 0);
        set(targetFile, "uploadTotalBytes", safeTotal || 0);
        set(
          targetFile,
          "uploadProgress",
          safeTotal ? Math.min(100, ((loaded || 0) / safeTotal) * 100) : 0
        );
      }
      updateOverallUploadProgress(state);
    },
    markActiveUploadsInterrupted(state) {
      state.uploadedFiles.forEach((uploadedFile) => {
        if (!uploadedFile.isUploaded && uploadedFile.isUploading) {
          set(uploadedFile, "isUploading", false);
          set(uploadedFile, "isUploadFailed", true);
          if (uploadedFile.uploadJobId) {
            set(uploadedFile, "uploadControlStatus", "failed");
          }
          set(uploadedFile, "uploadError", UPLOAD_INTERRUPTED_MESSAGE);
          set(uploadedFile, "retryUnavailable", false);
        }
      });
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    attachManagedUploadJob(state, { payload }) {
      const targetFile = getUploadedFileById(state, payload.idForComparison);
      if (!targetFile) return;
      set(targetFile, "uploadJobId", payload.jobId);
      set(targetFile, "uploadControlStatus", payload.status || "queued");
    },
    hydratePersistedUploads(state, { payload }) {
      (payload.jobs || []).forEach((job) => {
        if (!job || !job.transferRecord || !job.id) return;
        const existing = getUploadedFileById(
          state,
          job.transferRecord.idForComparison
        );
        if (existing) return;
        state.uploadedFiles.push({
          ...job.transferRecord,
          uploadJobId: job.id,
          uploadControlStatus: job.status,
          uploadLoadedBytes: job.completedBytes || 0,
          uploadTotalBytes: job.totalBytes || 0,
          uploadProgress: job.totalBytes
            ? ((job.completedBytes || 0) / job.totalBytes) * 100
            : 0,
          isUploading: job.status === "running",
          isUploadFailed: job.status === "failed",
          uploadError: job.error || "",
        });
        if (job.destinationProjectId) {
          state.selectedProjectId = job.destinationProjectId;
          state.activeUploadDestinationProjectId = job.destinationProjectId;
        }
      });
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    syncPersistedUploadJob(state, { payload }) {
      const { job } = payload;
      if (!job) return;
      const targetFile = state.uploadedFiles.find(
        (uploadedFile) =>
          uploadedFile.uploadJobId === job.id ||
          uploadedFile.idForComparison === job.id
      );
      if (!targetFile) return;
      set(targetFile, "uploadJobId", job.id);
      set(targetFile, "uploadControlStatus", job.status);
      set(targetFile, "uploadLoadedBytes", job.completedBytes || 0);
      set(targetFile, "uploadTotalBytes", job.totalBytes || 0);
      set(
        targetFile,
        "uploadProgress",
        job.totalBytes ? ((job.completedBytes || 0) / job.totalBytes) * 100 : 0
      );
      set(
        targetFile,
        "isUploading",
        job.status === "running" || job.status === "uploaded"
      );
      set(targetFile, "isUploadFailed", job.status === "failed");
      set(targetFile, "uploadError", job.error || "");
      set(targetFile, "isUploadCancelled", job.status === "cancelled");
      updateOverallUploadProgress(state);
      syncActiveUploadDestinationLock(state);
    },
    setCurrentUploadedFile(state, { payload }) {
      const { uploadedFile } = payload;
      state.currentUploadedFile = uploadedFile;
    },
    setProgressStatus(state, { payload }) {
      state.setProgressStatus = payload;
    },
    onConfirmTransferStart(state) {
      state.isTransfering = true;
    },
    onConfirmTransferSuccess(state) {
      state.isTransfering = false;
    },
    onConfirmTransferFailure(state, { payload }) {
      state.isTransfering = false;
      state.error = payload.error;
    },
    setIdea(state, { payload }) {
      const { idea } = payload;
      const { selectedProjectId: projectId } = state;
      const type = getReferenceType(idea.url);
      state.idea = state.idea
        ? { ...state.idea, ...idea, type }
        : {
            url: linkCleaner(idea.url),
            projectId,
            comment: "Have a look at this!",
            type,
            ...idea,
          };
    },
    setIdeaType(state, { payload }) {
      const { type } = payload;
      state.idea.type = type;
    },
    setIdeaComment(state, { payload }) {
      const { comment } = payload;
      state.idea.comment = comment;
    },
    startLoadingComponentsForProject(state, { payload }) {
      state.isLoadingComponentForProject = payload.projectId;
    },
    finishLoadingComponentsForProject(state, { payload }) {
      state.isLoadingComponentForProject = null;
      if (payload) {
        state.error = payload.error;
      }
    },
    startLoadingAllComponentsUploadedByUser(state) {
      state.isLoadingAllComponentsUploadedByUser = true;
    },
    finishLoadingAllComponentsUploadedByUser(state, { payload }) {
      state.isLoadingAllComponentsUploadedByUser = false;
      if (payload) {
        const { error } = payload;
        state.errorGettingAllComponentsUploadedByUser = error;
      }
    },
    setComponentsNotInCurrentProject(state, { payload }) {
      state.componentsNotInCurrentProject =
        payload.componentsNotInCurrentProject;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(LOCATION_CHANGE, (state, { payload }) => {
      const {
        location: { pathname },
      } = payload;
      state.location = getLocation(pathname);
    });
  },
});

export const {
  onLoaded,
  openPanel,
  openPanelForNewVersion,
  setAllProjectsGroupedComponents,
  addUploadsFiles,
  setUploadedComponentIsIdeaOrNot,
  renameUploadedComponent,
  removeUploadedComponent,
  setStartedUploadingComponent,
  addImportedComponents,
  setImportedComponentIsIdeaOrNot,
  renameImportedComponent,
  removeImportedComponent,
  setComponentVersion,
  completeUploadForUploadedFile,
  completeFolderPackageCommitForUploadedFile,
  completeFolderPackagingForUploadedFile,
  failUploadForUploadedFile,
  setFolderPackagingStatus,
  setSelectedProject,
  createProject,
  setSelectedComponentToShare,
  shareComponent,
  stopShareComponent,
  inviteToProject,
  stopInviteToProject,
  selectTask,
  hidePanel,
  closePanel,
  handleGeneralUploadProgression,
  markActiveUploadsInterrupted,
  attachManagedUploadJob,
  hydratePersistedUploads,
  syncPersistedUploadJob,
  setCurrentUploadedFile,
  setProgressStatus,
  onConfirmTransferStart,
  onConfirmTransferSuccess,
  onConfirmTransferFailure,
  setIdea,
  setIdeaType,
  setIdeaComment,
  startLoadingComponentsForProject,
  finishLoadingComponentsForProject,
  startLoadingAllComponentsUploadedByUser,
  finishLoadingAllComponentsUploadedByUser,
  setComponentsNotInCurrentProject,
  removeIdeasFromDashboard,
  setSelectableVersionsOptions,
  resetVersionsIfFromDifferentProject,
} = transferManager.actions;

export default transferManager.reducer;

// THUNKS //

export const clearTransferPanel = ({ accountId, uploadedFiles }) => async (
  dispatch
) => {
  try {
    const deletePromises = [];
    uploadedFiles.forEach((uploadedFile) => {
      const fileUris = getUploadedFileUris(uploadedFile);
      if (!fileUris.length) {
        return;
      }
      fileUris.forEach((fileUri) => {
        deletePromises.push(
          removeFileToS3(fileUri, accountId).catch((e) =>
            handleError(e.message)
          )
        )
      });
    });
    await Promise.all(deletePromises);
    clearCachedUploadFiles(uploadedFiles);
    dispatch(closePanel());
  } catch (e) {
    handleError(e);
  }
};

function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function getFolderPackageJob(uploadedFile) {
  return get(uploadedFile, "folderPackagingJob", {});
}

function buildProtectedFolderPackagingKey(accountId, key) {
  return "protected/" + accountId + "/" + key;
}

function removeProtectedFolderPackagingKeyPrefix(key, accountId) {
  if (!key || typeof key !== "string") return "";

  const accountPrefix = accountId ? "protected/" + accountId + "/" : "";
  if (accountPrefix && key.startsWith(accountPrefix)) {
    return key.substring(accountPrefix.length);
  }

  if (key.startsWith("protected/")) {
    return key
      .split("/")
      .slice(2)
      .join("/");
  }

  return key;
}

function getFolderPackagingAppFileKey(job, accountId) {
  const finalFileKey = removeProtectedFolderPackagingKeyPrefix(
    get(job, "finalFileKey", ""),
    accountId
  );
  if (finalFileKey) return finalFileKey;

  return removeProtectedFolderPackagingKeyPrefix(
    get(job, "fullFinalFileKey", ""),
    accountId
  );
}

function getFolderPackageDescriptorKey(jobId) {
  return `folder-packages/${jobId}/package.json`;
}

function getFolderPackageMetadata({
  job = {},
  manifestKey,
  rawPrefix,
  descriptorKey,
  status,
}) {
  return {
    folderPackageJobId: get(job, "jobId", ""),
    folderPackageStatus: status || get(job, "status", ""),
    folderPackageManifestKey: manifestKey || get(job, "manifestKey", ""),
    folderPackageRawPrefix: rawPrefix || get(job, "rawPrefix", ""),
    folderPackageDescriptorKey: descriptorKey,
    folderPackageFinalFileKey: get(job, "finalFileKey", ""),
    folderPackageFullFinalFileKey: get(job, "fullFinalFileKey", ""),
    folderPackageOutputSize: get(job, "outputSize", ""),
    folderPackageErrorCode: get(job, "errorCode", ""),
    folderPackageErrorMessage: get(job, "errorMessage", ""),
    folderPackageItemCount: Number(get(job, "itemCount", 0)) || null,
    folderPackageTotalBytes: String(get(job, "totalBytes", "0") || "0"),
    folderPackageFolderName: get(job, "folderName", ""),
  };
}

function stripTransientUploadFields(uploadedFile) {
  const uploadState = { ...uploadedFile };
  delete uploadState.fileIndex;
  delete uploadState.folderFileIndexes;
  return uploadState;
}

function getComponentIdFromFileKey(fileKey) {
  return String(fileKey || "").split("/")[1] || "";
}

function getSafeFolderRelativePath(relativePath) {
  if (typeof relativePath !== "string" || !relativePath) {
    throw new Error("Folder file path is missing");
  }
  const segments = relativePath.split("/").filter((segment) => segment.length);
  if (
    segments.length === 0 ||
    segments.some((segment) => segment === "." || segment.includes(".."))
  ) {
    throw new Error("Folder file path is not safe");
  }
  return segments.join("/");
}

function createFolderManifestFile({ uploadedFile, rawPrefix }) {
  const job = getFolderPackageJob(uploadedFile);
  const manifest = {
    ...get(job, "manifest", {}),
    jobId: job.jobId,
    rawPrefix,
    createdAt: new Date().toISOString(),
  };
  return new Blob([JSON.stringify(manifest, null, 2)], {
    type: "application/json",
  });
}

function createFolderPackageDescriptorFile({
  uploadedFile,
  accountId,
  descriptorKey,
  manifestKey,
  rawPrefix,
  projectId,
  totalBytes,
}) {
  const job = getFolderPackageJob(uploadedFile);
  const descriptor = {
    version: FOLDER_PACKAGE_DESCRIPTOR_VERSION,
    jobId: job.jobId,
    accountId,
    projectId: projectId === "Dashboard" ? null : projectId || null,
    folderName: job.folderName,
    classification: job.classification,
    itemCount: Number(job.itemCount) || 0,
    totalBytes: String(job.totalBytes || totalBytes || 0),
    descriptorKey: buildProtectedFolderPackagingKey(accountId, descriptorKey),
    manifestKey: buildProtectedFolderPackagingKey(accountId, manifestKey),
    rawPrefix: buildProtectedFolderPackagingKey(accountId, rawPrefix),
    createdAt: new Date().toISOString(),
  };
  return new Blob([JSON.stringify(descriptor, null, 2)], {
    type: FOLDER_PACKAGE_DESCRIPTOR_CONTENT_TYPE,
  });
}

async function pollFolderPackagingJob({
  dispatch,
  getState,
  idForComparison,
  uploadAttemptId,
  personId,
  accountId,
  jobId,
}) {
  for (let attempt = 0; attempt < FOLDER_PACKAGING_MAX_POLL_ATTEMPTS; attempt++) {
    if (attempt > 0) {
      await delay(FOLDER_PACKAGING_POLL_INTERVAL_MS);
    }
    const {
      transferManager: { uploadedFiles },
    } = getState();
    const currentFile = uploadedFiles.find(
      (file) => file.idForComparison === idForComparison
    );
    if (
      !currentFile ||
      currentFile.isUploadFailed ||
      get(currentFile, "folderPackagingStatus", "") ===
        FOLDER_PACKAGING_STATUS.PACKAGING_READY
    ) {
      return;
    }

    let job;
    try {
      job = await getFolderPackagingJob({ jobId, personId, accountId });
    } catch (error) {
      if (attempt < FOLDER_PACKAGING_MAX_POLL_ATTEMPTS - 1) {
        continue;
      }
      dispatch(
        setFolderPackagingStatus({
          idForComparison,
          uploadAttemptId,
          status: FOLDER_PACKAGING_STATUS.PACKAGING_FAILED,
          error: getUploadErrorMessage(error),
        })
      );
      return;
    }
    if (!job) return;

    if (job.status === "FAILED") {
      dispatch(
        setFolderPackagingStatus({
          idForComparison,
          uploadAttemptId,
          status: FOLDER_PACKAGING_STATUS.PACKAGING_FAILED,
          job,
          error: job.errorMessage || "Folder packaging failed. Please retry.",
        })
      );
      return;
    }

    if (job.status === "READY") {
      const fileKey = getFolderPackagingAppFileKey(job, accountId);
      if (!fileKey) {
        dispatch(
          setFolderPackagingStatus({
            idForComparison,
            uploadAttemptId,
            status: FOLDER_PACKAGING_STATUS.PACKAGING_FAILED,
            job,
            error: "Folder packaging completed without a file key.",
            retryUnavailable: true,
          })
        );
        return;
      }
      dispatch(
        completeFolderPackagingForUploadedFile({
          idForComparison,
          uploadAttemptId,
          accountId,
          job,
        })
      );
      forgetCachedUploadFile(idForComparison);
      return;
    }

    dispatch(
      setFolderPackagingStatus({
        idForComparison,
        uploadAttemptId,
        status: FOLDER_PACKAGING_STATUS.PACKAGING_PENDING,
        job,
      })
    );
  }
}

export async function uploadFolderPackage({
  dispatch,
  getState,
  uploadedFile,
  accountId,
  personId,
  rawFiles,
}) {
  const idForComparison = getUploadFileCacheKey(uploadedFile);
  const uploadAttemptId = uuid();
  const job = getFolderPackageJob(uploadedFile);
  const jobId = job.jobId;
  const rawPrefix = `folder-staging/${jobId}/raw/`;
  const manifestKey = `folder-staging/${jobId}/manifest.json`;
  const descriptorKey = getFolderPackageDescriptorKey(jobId);

  if (!rawFiles || !rawFiles.length) {
    dispatch(
      setFolderPackagingStatus({
        idForComparison,
        status: FOLDER_PACKAGING_STATUS.PACKAGING_FAILED,
        error:
          "The original folder files are no longer available. Please add the folder again.",
        retryUnavailable: true,
      })
    );
    return false;
  }

  const totalBytes = rawFiles.reduce(
    (total, rawFile) => total + (Number(rawFile.size) || 0),
    0
  );

  try {
    dispatch(setStartedUploadingComponent({ idForComparison, uploadAttemptId }));
    dispatch(
      setFolderPackagingStatus({
        idForComparison,
        uploadAttemptId,
        status: FOLDER_PACKAGING_STATUS.UPLOADING_RAW_FILES,
      })
    );

    const stagedRawFiles = rawFiles.map((rawFile) => {
      const safeRelativePath = getSafeFolderRelativePath(rawFile.relativePath);
      return {
        ...rawFile,
        safeRelativePath,
        key: rawPrefix + safeRelativePath,
      };
    });
    const manifestFile = createFolderManifestFile({
      uploadedFile,
      rawPrefix: buildProtectedFolderPackagingKey(accountId, rawPrefix),
    });
    const {
      transferManager: { activeUploadDestinationProjectId, selectedProjectId },
    } = getState();
    const uploadDestinationProjectId =
      activeUploadDestinationProjectId || selectedProjectId;
    const descriptorFile = createFolderPackageDescriptorFile({
      uploadedFile,
      accountId,
      descriptorKey,
      manifestKey,
      rawPrefix,
      projectId: uploadDestinationProjectId,
      totalBytes,
    });
    const managedResult = await runManagedS3Upload({
      dispatch,
      uploadedFile,
      accountId,
      uploadAttemptId,
      destinationProjectId: uploadDestinationProjectId,
      sources: [
        ...stagedRawFiles.map((rawFile, index) => ({
          id: `${idForComparison}:raw:${index}`,
          jobId: idForComparison,
          storageKey: rawFile.key,
          name: rawFile.safeRelativePath,
          size: Number(rawFile.size) || 0,
          type: rawFile.type || "application/octet-stream",
          lastModified: rawFile.lastModified || 0,
          relativePath: rawFile.safeRelativePath,
          metadata: {
            contentLength: String(rawFile.size || 0),
            lastModified: String(rawFile.lastModified || ""),
            relativePath: rawFile.safeRelativePath,
          },
          status: "pending",
          completedParts: [],
          completedBytes: 0,
          blob: rawFile.file,
        })),
        {
          id: `${idForComparison}:manifest`,
          jobId: idForComparison,
          storageKey: manifestKey,
          name: "manifest.json",
          size: manifestFile.size,
          type: "application/json",
          metadata: {
            contentLength: String(manifestFile.size || 0),
            folderName: job.folderName || "",
          },
          status: "pending",
          completedParts: [],
          completedBytes: 0,
          blob: manifestFile,
        },
        {
          id: `${idForComparison}:descriptor`,
          jobId: idForComparison,
          storageKey: descriptorKey,
          name: "package.json",
          size: descriptorFile.size,
          type: FOLDER_PACKAGE_DESCRIPTOR_CONTENT_TYPE,
          metadata: {
            contentLength: String(descriptorFile.size || 0),
            folderName: job.folderName || "",
            jobId,
          },
          status: "pending",
          completedParts: [],
          completedBytes: 0,
          blob: descriptorFile,
        },
      ],
      completion: {
        kind: "folderPackage",
        accountId,
        personId,
        idForComparison,
        uploadAttemptId,
        jobId,
        descriptorKey,
        manifestKey: buildProtectedFolderPackagingKey(accountId, manifestKey),
        rawPrefix: buildProtectedFolderPackagingKey(accountId, rawPrefix),
        uploadDestinationProjectId,
        folderName: job.folderName,
        classification: job.classification,
        itemCount: job.itemCount,
        totalBytes: String(job.totalBytes || totalBytes || 0),
      },
    });
    if (get(managedResult, "job.status", "") !== "uploaded") return false;
    await completeManagedUpload(dispatch, getState, managedResult.job);
    return false;
  } catch (error) {
    if (error && error.name === "UploadCancelledError") return false;
    dispatch(
      setFolderPackagingStatus({
        idForComparison,
        uploadAttemptId,
        status: FOLDER_PACKAGING_STATUS.PACKAGING_FAILED,
        error: getUploadErrorMessage(error),
      })
    );
    handleError(error);
    return false;
  }
}

async function uploadFolderRelativePathFiles({
  dispatch,
  getState,
  uploadedFile,
  accountId,
  childFiles,
}) {
  const idForComparison = getUploadFileCacheKey(uploadedFile);
  const uploadAttemptId = uuid();
  const componentId = get(uploadedFile, "payload.componentInput.id", "") || uuid();
  const folderRoot = get(uploadedFile, "payload.componentInput.folderRoot", "");
  const folderUploadId =
    get(uploadedFile, "payload.componentInput.folderUploadId", "") || uuid();

  if (!childFiles || !childFiles.length) {
    dispatch(
      failUploadForUploadedFile({
        idForComparison,
        error:
          "The original folder files are no longer available. Please add the folder again.",
        retryUnavailable: true,
      })
    );
    return false;
  }

  const stagedFiles = childFiles.map((childFile, index) => {
    const fileInput = {
      ...get(uploadedFile, `payload.fileInputs[${index}]`, {}),
      ...childFile.fileInput,
    };
    const relativePath = getSafeFolderRelativePath(fileInput.relativePath);
    const storageFolder = uploadedFile.sourceFile ? "file/" : "audio/";
    return {
      ...childFile,
      fileInput: {
        ...fileInput,
        relativePath,
        folderRoot,
        folderUploadId,
      },
      progressKey: `${index}:${relativePath}`,
      key: storageFolder + componentId + "/" + relativePath,
    };
  });

  try {
    dispatch(setStartedUploadingComponent({ idForComparison, uploadAttemptId }));
    dispatch(setCurrentUploadedFile({ uploadedFile }));

    const fileInputs = stagedFiles.map((stagedFile) => ({
      ...stagedFile.fileInput,
      uri: stagedFile.key,
    }));
    const fileKey = get(fileInputs, "[0].uri", "");
    const managedResult = await runManagedS3Upload({
      dispatch,
      uploadedFile,
      accountId,
      uploadAttemptId,
      destinationProjectId:
        getState().transferManager.activeUploadDestinationProjectId ||
        getState().transferManager.selectedProjectId,
      sources: stagedFiles.map((stagedFile, index) => ({
        id: `${idForComparison}:${index}`,
        jobId: idForComparison,
        storageKey: stagedFile.key,
        name: get(stagedFile, "file.name", stagedFile.fileInput.fileName),
        size: Number(get(stagedFile, "file.size", 0)) || 0,
        type:
          get(stagedFile, "file.type", "") ||
          stagedFile.fileInput.mimeType ||
          "application/octet-stream",
        lastModified: get(stagedFile, "file.lastModified", 0),
        relativePath: stagedFile.fileInput.relativePath,
        metadata: {
          contentLength: String(get(stagedFile, "file.size", 0) || 0),
          lastModified: String(get(stagedFile, "file.lastModified", "") || ""),
          relativePath: stagedFile.fileInput.relativePath,
          folderRoot,
          folderUploadId,
        },
        status: "pending",
        completedParts: [],
        completedBytes: 0,
        blob: stagedFile.file,
      })),
      completion: {
        kind: "folder",
        accountId,
        idForComparison,
        uploadAttemptId,
        componentId,
        fileKey,
        fileInputs,
      },
    });
    if (get(managedResult, "job.status", "") !== "uploaded") return false;
    await completeManagedUpload(dispatch, getState, managedResult.job);
    forgetCachedUploadFile(getUploadFileCacheKey(uploadedFile));

    const {
      transferManager: { selectedComponentIdToShare, selectedProjectId },
    } = getState();
    if (!selectedComponentIdToShare && selectedProjectId !== "Dashboard") {
      dispatch(setSelectedComponentToShare());
    }

    return true;
  } catch (error) {
    if (error && error.name === "UploadCancelledError") return false;
    dispatch(
      failUploadForUploadedFile({
        idForComparison,
        uploadAttemptId,
        error: getUploadErrorMessage(error),
      })
    );
    handleError(error);
    return false;
  } finally {
    dispatch(setCurrentUploadedFile({ uploadedFile: null }));
  }
}

async function runManagedS3Upload({
  dispatch,
  uploadedFile,
  accountId,
  uploadAttemptId,
  destinationProjectId,
  sources,
  completion,
}) {
  const manager = await getUploadManager();
  const now = new Date().toISOString();
  const totalBytes = sources.reduce(
    (total, source) => total + (Number(source.size) || 0),
    0
  );
  const job = {
    id: getUploadFileCacheKey(uploadedFile),
    direction: "upload",
    type:
      uploadedFile.isFolderUpload || uploadedFile.isFolderPackage
        ? "folder"
        : "file",
    title: get(uploadedFile, "payload.componentInput.title", "Upload"),
    status: "queued",
    accountId,
    destinationProjectId,
    personId: get(uploadedFile, "payload.partyId", ""),
    totalBytes,
    completedBytes: 0,
    sourceCount: sources.length,
    transferRecord: {
      ...uploadedFile,
      uploadAttemptId,
      uploadJobId: getUploadFileCacheKey(uploadedFile),
    },
    completion,
    createdAt: now,
    updatedAt: now,
  };

  await requestPersistentUploadStorage();
  const sessionRecord = await manager.startUpload({ job, sources });
  dispatch(registerUpload(job));
  dispatch(
    attachManagedUploadJob({
      idForComparison: job.id,
      jobId: job.id,
      status: "running",
    })
  );
  dispatch(
    updateUpload({
      ...job,
      status: "running",
      updatedAt: new Date().toISOString(),
    })
  );
  const result = await sessionRecord.runPromise;
  if (result && result.job) {
    dispatch(updateUpload(result.job));
    dispatch(syncPersistedUploadJob({ job: result.job }));
  }
  return result;
}

async function uploadPreparedFile({
  dispatch,
  getState,
  uploadedFile,
  accountId,
  personId,
  file,
}) {
  if (uploadedFile.isFolderPackage) {
    return uploadFolderPackage({
      dispatch,
      getState,
      uploadedFile,
      accountId,
      personId,
      rawFiles: getCachedFolderPackageFiles(uploadedFile),
    });
  }

  if (uploadedFile.isFolderUpload) {
    return uploadFolderRelativePathFiles({
      dispatch,
      getState,
      uploadedFile,
      accountId,
      childFiles: getCachedFolderUploadFiles(uploadedFile),
    });
  }

  const idForComparison = getUploadFileCacheKey(uploadedFile);
  const uploadAttemptId = uuid();

  if (!file) {
    dispatch(
      failUploadForUploadedFile({
        idForComparison,
        error:
          "The original file is no longer available. Please add it again.",
        retryUnavailable: true,
      })
    );
    return false;
  }

  try {
    dispatch(setStartedUploadingComponent({ idForComparison, uploadAttemptId }));
    dispatch(setCurrentUploadedFile({ uploadedFile }));
    const componentId = uuid();
    const fileName = file.name.trim().replace(/\//gi, "");
    const storageFolder = uploadedFile.isSourceFile ? "file/" : "audio/";
    const fileKey = `${storageFolder}${componentId}/${fileName}`;
    const managedResult = await runManagedS3Upload({
      dispatch,
      uploadedFile,
      accountId,
      uploadAttemptId,
      destinationProjectId:
        getState().transferManager.activeUploadDestinationProjectId ||
        getState().transferManager.selectedProjectId,
      sources: [
        {
          id: `${idForComparison}:0`,
          jobId: idForComparison,
          storageKey: fileKey,
          name: file.name,
          size: Number(file.size) || 0,
          type:
            file.type ||
            get(uploadedFile, "storageInput.contentType", "") ||
            "application/octet-stream",
          lastModified: file.lastModified || 0,
          metadata: get(uploadedFile, "storageInput.metadata", {}),
          status: "pending",
          completedParts: [],
          completedBytes: 0,
          blob: file,
        },
      ],
      completion: {
        kind: "file",
        accountId,
        idForComparison,
        uploadAttemptId,
        componentId,
        fileKey,
      },
    });
    if (get(managedResult, "job.status", "") !== "uploaded") return false;
    await completeManagedUpload(dispatch, getState, managedResult.job);
    const {
      transferManager: { uploadedFiles },
    } = getState();
    const completedFile = uploadedFiles.find(
      (file) => file.idForComparison === idForComparison
    );
    if (get(completedFile, "payload.fileInput.uri", "") !== fileKey) {
      return false;
    }

    forgetCachedUploadFile(getUploadFileCacheKey(uploadedFile));

    const {
      transferManager: { selectedComponentIdToShare, selectedProjectId },
    } = getState();
    if (!selectedComponentIdToShare && selectedProjectId !== "Dashboard") {
      dispatch(setSelectedComponentToShare());
    }

    return true;
  } catch (error) {
    if (error && error.name === "UploadCancelledError") return false;
    dispatch(
      failUploadForUploadedFile({
        idForComparison,
        uploadAttemptId,
        error: getUploadErrorMessage(error),
      })
    );
    handleError(error);
    return false;
  } finally {
    dispatch(setCurrentUploadedFile({ uploadedFile: null }));
  }
}

export const startUploadProcess = ({ personId, files, accountId }) => async (
  dispatch,
  getState
) => {
  try {
    const {
      transferManager: {
        targetParentComponent,
        targetLatestVersionComponent,
        selectedProjectId,
        location,
      },
    } = getState();

    const newUploadedFilesWithTransientFields = getPayloads({
      files,
      personId,
      targetParentComponent,
      targetLatestVersionComponent,
    });

    newUploadedFilesWithTransientFields.forEach((uploadedFile, index) => {
      if (uploadedFile.isFolderPackage) {
        cacheFolderPackageFiles(
          uploadedFile,
          get(files[get(uploadedFile, "fileIndex", index)], "rawFiles", [])
        );
        return;
      }
      if (uploadedFile.isFolderUpload) {
        const childFiles = get(uploadedFile, "folderFileIndexes", []).map(
          (fileIndex, childIndex) => ({
            file: get(files[fileIndex], "file"),
            fileInput: get(uploadedFile, `payload.fileInputs[${childIndex}]`, {}),
          })
        );
        cacheFolderUploadFiles(uploadedFile, childFiles);
        return;
      }
      cacheUploadFile(
        uploadedFile,
        get(files[get(uploadedFile, "fileIndex", index)], "file")
      );
    });

    const newUploadedFiles = newUploadedFilesWithTransientFields.map(
      stripTransientUploadFields
    );

    dispatch(addUploadsFiles({ uploadedFiles: newUploadedFiles }));

    const {
      transferManager: {
        uploadedFiles, // this must be here to get updated uploadedFiles
      },
    } = getState();

    if (selectedProjectId === "Dashboard") {
      uploadedFiles.forEach((item, position) => {
        if (item.isIdea) {
          dispatch(
            setUploadedComponentIsIdeaOrNot({ position, isIdea: false })
          );
        }
      });
    }
    uploadedFiles.forEach((item, position) => {
      //If in idea panel then set the default to idea
      if (location.ideas) {
        dispatch(setUploadedComponentIsIdeaOrNot({ position, isIdea: true }));
      } else if (item.isVersion && targetParentComponent) {
        const isSameType = isVersionUploadTypeCompatible(
          item.label,
          targetParentComponent["__typename"]
        );
        // Reject incompatible explicit version uploads instead of silently
        // converting them into unrelated V1 files.
        if (!isSameType) {
          handleError(INCOMPATIBLE_VERSION_UPLOAD_MESSAGE);
          forgetCachedUploadFile(item.idForComparison);
          dispatch(
            removeUploadedComponent({
              idForComparison: item.idForComparison,
            })
          );
        }
      }
    });

    await runUploadBatch(newUploadedFiles, async (uploadedFile) => {
      const {
        transferManager: { uploadedFiles: currentUploadedFiles },
      } = getState();
      const currentUploadedFile = currentUploadedFiles.find(
        (file) => file.idForComparison === uploadedFile.idForComparison
      );
      if (
        !currentUploadedFile ||
        currentUploadedFile.isUploaded ||
        currentUploadedFile.isUploading
      ) {
        return false;
      }

      return uploadPreparedFile({
        dispatch,
        getState,
        uploadedFile: currentUploadedFile,
        accountId,
        personId,
        file: getCachedUploadFile(currentUploadedFile),
      });
    });
  } catch (e) {
    handleError(e);
  }
};

export const retryUpload = ({ accountId, idForComparison }) => async (
  dispatch,
  getState
) => {
  const {
    transferManager: { uploadedFiles },
  } = getState();
  const position = uploadedFiles.findIndex(
    (uploadedFile) => uploadedFile.idForComparison === idForComparison
  );
  const uploadedFile = uploadedFiles[position];
  if (!uploadedFile || uploadedFile.isUploaded || uploadedFile.isUploading) {
    return;
  }

  await uploadPreparedFile({
    dispatch,
    getState,
    uploadedFile,
    accountId,
    personId: get(uploadedFile, "payload.partyId", ""),
    file: getCachedUploadFile(uploadedFile),
  });
};

async function getOrStartFolderPackaging(completion) {
  try {
    const existingJob = await getFolderPackagingJob({
      jobId: completion.jobId,
      personId: completion.personId,
      accountId: completion.accountId,
    });
    if (existingJob) return existingJob;
  } catch (error) {
    // The packaging job does not exist yet, so create it below.
  }

  return startFolderPackaging({
    personId: completion.personId,
    accountId: completion.accountId,
    projectId:
      completion.uploadDestinationProjectId === "Dashboard"
        ? null
        : completion.uploadDestinationProjectId,
    folderName: completion.folderName,
    classification: completion.classification,
    itemCount: completion.itemCount,
    totalBytes: completion.totalBytes,
    manifestKey: completion.manifestKey,
    rawPrefix: completion.rawPrefix,
  });
}

async function completeManagedUpload(dispatch, getState, job) {
  if (!job || !job.completion) return null;
  const manager = await getUploadManager();

  try {
    if (job.completion.kind === "file" || job.completion.kind === "folder") {
      dispatch(completeUploadForUploadedFile(job.completion));
    } else if (job.completion.kind === "folderPackage") {
      const completion = job.completion;
      dispatch(
        setFolderPackagingStatus({
          idForComparison: completion.idForComparison,
          uploadAttemptId: completion.uploadAttemptId,
          status: FOLDER_PACKAGING_STATUS.CREATING_PACKAGING_JOB,
        })
      );
      const packagingJob = await getOrStartFolderPackaging(completion);
      dispatch(
        completeFolderPackageCommitForUploadedFile({
          accountId: completion.accountId,
          descriptorKey: completion.descriptorKey,
          idForComparison: completion.idForComparison,
          manifestKey: completion.manifestKey,
          rawPrefix: completion.rawPrefix,
          uploadAttemptId: completion.uploadAttemptId,
          job: packagingJob,
        })
      );
      pollFolderPackagingJob({
        dispatch,
        getState,
        idForComparison: completion.idForComparison,
        uploadAttemptId: completion.uploadAttemptId,
        personId: completion.personId,
        accountId: completion.accountId,
        jobId: packagingJob.jobId || completion.jobId,
      }).catch(handleError);
    }

    const completedJob = await manager.finalizeUpload({ jobId: job.id });
    if (completedJob) {
      dispatch(updateUpload(completedJob));
      dispatch(syncPersistedUploadJob({ job: completedJob }));
    }
    return completedJob;
  } catch (error) {
    const pausedJob = await manager.pauseUpload({ jobId: job.id });
    if (pausedJob) {
      dispatch(updateUpload(pausedJob));
      dispatch(syncPersistedUploadJob({ job: pausedJob }));
    }
    throw error;
  }
}

export const pauseManagedUpload = ({ jobId }) => async (dispatch) => {
  const manager = await getUploadManager();
  const job = await manager.pauseUpload({ jobId });
  if (job) {
    dispatch(updateUpload(job));
    dispatch(syncPersistedUploadJob({ job }));
  }
  return job;
};

export const resumeManagedUpload = ({ jobId }) => async (
  dispatch,
  getState
) => {
  const manager = await getUploadManager();
  const wasAlreadyActive =
    typeof manager.getActiveUploads === "function" &&
    manager.getActiveUploads().some((record) => record.jobId === jobId);
  const sessionRecord = manager.resumeUpload({ jobId });
  const runningJob = {
    ...(selectUploadById(getState(), jobId) || {}),
    id: jobId,
    status: "running",
    updatedAt: new Date().toISOString(),
  };
  dispatch(updateUpload(runningJob));
  dispatch(syncPersistedUploadJob({ job: runningJob }));

  const runPromise = Promise.resolve(sessionRecord.runPromise).then(
    async (result) => {
      const uploadedJob = get(result, "job", null);
      if (
        uploadedJob &&
        uploadedJob.status === "uploaded" &&
        !wasAlreadyActive
      ) {
        await completeManagedUpload(dispatch, getState, uploadedJob);
      }
      return result;
    }
  );
  runPromise.catch((error) => {
    handleError(error);
  });

  // Starting the session is the end of the Resume UI action. Completion keeps
  // running in the background so the control can immediately become Pause.
  return {
    ...sessionRecord,
    runPromise,
  };
};

export const cancelManagedUpload = ({ jobId }) => async (dispatch) => {
  const manager = await getUploadManager();
  const job = await manager.cancelUpload({ jobId });
  if (job) {
    dispatch(updateUpload(job));
    dispatch(syncPersistedUploadJob({ job }));
    if (job.status === "cancelled") {
      forgetCachedUploadFile(jobId);
      dispatch(removeUploadedComponent({ idForComparison: jobId }));
    }
  }
  return job;
};

function createGroupWrappings(
  payload,
  selectedProjectId,
  sameSpaceComponentsByGroupAndOrdered
) {
  const componentGroup = payload.componentGroup;
  const {
    componentOrderWeight,
    componentGroupOrderWeight,
  } = getComponentGroupAndWeights({
    componentGroup,
    selectedProjectId,
    sameSpaceComponentsByGroupAndOrdered,
  });
  return {
    componentGroup: payload.componentGroup,
    componentGroupOrderWeight,
    componentOrderWeight,
    component: [],
  };
}

export const onConfirmTransfer = (payload) => async (dispatch, getState) => {
  const { personId } = payload;
  dispatch(onConfirmTransferStart());
  const {
    transferManager: {
      projectInvitationPayload,
      uploadedFiles,
      importedComponents,
      isNewProject,
      newProjectName,
      selectedProjectId,
      selectedComponentIdToShare,
      sharedComponentUri,
      selectedTaskId,
      allProjectsGroupedComponents,
      idea,
    },
  } = getState();

  const uploadedFilesReadyForTransfer = uploadedFiles.filter(
    isUploadedFileReadyForTransfer
  );

  let isAnyWorkUploaded = false;

  let project;
  if (isNewProject) {
    try {
      const payload = {
        projectId: selectedProjectId,
        title: newProjectName,
        status: "New",
        partyId: personId,
      };
      project = await createEntity("Project", payload);
      if (!project || (project && !project.id)) {
        handleError(
          "Something went wrong while trying to create a new project"
        );
        dispatch(
          onConfirmTransferFailure({
            error: "Something went wrong while trying to create a new project",
          })
        );
        return;
      }
    } catch (err) {
      handleError(
        "Something went wrong while trying to create a new project, error: " +
          err
      );
      dispatch(onConfirmTransferFailure({ error: err.toString() }));
      return;
    }
  }

  let savedIdea;

  if (idea) {
    try {
      const payload = {
        url: idea.url,
        projectId: selectedProjectId,
        personId: idea.personId,
      };
      savedIdea = await createNewIdeaWithComment({
        type: idea.type ? idea.type : "Text",
        payload,
        comment: idea.comment,
      });
    } catch (e) {
      dispatch(onConfirmTransferFailure({ error: e.toString() }));
      handleError(e);
    }
  }
  const groupedComponents = {};
  const transferInputs = uploadedFilesReadyForTransfer.concat(importedComponents);

  const sameSpaceComponentsByGroupAndOrdered =
    selectedProjectId !== "Dashboard" &&
    getComponentsInSelectedProjectByGroupAndOrdered({
      projectId: selectedProjectId,
      groupedComponents: getSameSpaceGroupedComponents(
        selectedProjectId,
        allProjectsGroupedComponents
      ),
    });

  // 1. Create promises with ideas and components
  const promises = transferInputs
    .map((input, i) => {
      const { payload, isIdea } = input;
      const isUploadedInput = i < uploadedFilesReadyForTransfer.length;

      if (!isIdea) {
        isAnyWorkUploaded = true;
      }

      groupedComponents[getUploadCorrelationKey(input)] = createGroupWrappings(
        payload,
        selectedProjectId,
        sameSpaceComponentsByGroupAndOrdered
      );

      return addInput({
        input,
        selectedProjectId,
        previouslyAddedFiles: isUploadedInput
          ? uploadedFilesReadyForTransfer.slice(0, i)
          : undefined,
        sameSpaceComponentsByGroupAndOrdered,
      });
    });

  try {
    // 2. post requests
    const results = await Promise.all(promises);

    // 3. Filter return types for type
    const newComponents = buildGroupedComponentsFromTransferResults({
      transferInputs,
      results,
      groupedComponents,
    });

    const references = getReferencesFromTransferResults({
      transferInputs,
      results,
    });

    // filter components from references
    const onlyComponents = newComponents.filter(
      (c) => references.find((r) => r.id === c.component[0].id) == null
    );

    //pass component with group data to conform to the schema
    const { components, result } = normalizeComponents(onlyComponents);

    // 4. Create shared component if selected
    let sharedComponent = null;
    const label = get(
      uploadedFilesReadyForTransfer.find(
        (comp) =>
          get(comp, "payload.componentInput.id") === selectedComponentIdToShare
      ),
      "label",
      get(
        importedComponents.find(
          (comp) =>
            get(comp, "payload.componentInput.id") ===
            selectedComponentIdToShare
        ),
        "label",
        ""
      )
    );
    if (selectedComponentIdToShare && sharedComponentUri) {
      sharedComponent = await createEntity("SharedComponentWithUri", {
        personId,
        componentId: selectedComponentIdToShare,
        shareComponentUri: sharedComponentUri,
        label,
      });
    }

    // 5. Create invitation if present
    let projectInvitation = null;
    if (projectInvitationPayload) {
      if (projectInvitationPayload.isInvite) {
        projectInvitation = await createEntity(
          "InvitationWithSharedComponent",
          projectInvitationPayload.payload
        );
      } else {
        projectInvitation = await addRelationship(
          "AddProjectMembersWithSharedComponent",
          projectInvitationPayload.payload
        );
      }
    }

    // 6. Add components to redux (there is a listener here)
    dispatch(
      onConfirmTransferSuccess({
        project,
        idea: savedIdea,
        references,
        components,
        result,
        selectedComponentIdToShare,
        sharedComponent,
        projectId: selectedProjectId,
        projectInvitation,
      })
    );
  } catch (e) {
    dispatch(onConfirmTransferFailure({ error: e.toString() }));
    handleError(e);
  }

  if (selectedTaskId) {
    await dispatch(
      updateTask({
        personId,
        projectId: selectedProjectId,
        input: { id: selectedTaskId, status: TaskStatus.DONE },
      })
    );
    if (selectedProjectId !== "Dashboard") {
      await dispatch(
        fetchTasksInProject({
          personId,
          projectId: selectedProjectId,
        })
      );
    }
  }

  dispatch(closePanel());
  const finalPath = isAnyWorkUploaded ? "/work" : "/ideas";
  if (selectedProjectId !== "Dashboard") {
    dispatch(push(`/project/${selectedProjectId}${finalPath}`));
  }
};

export const getAllComponentsUploadedByUser = ({ personId }) => async (
  dispatch,
  getState
) => {
  try {
    const {
      transferManager: { selectedProjectId },
    } = getState();
    dispatch(startLoadingAllComponentsUploadedByUser());

    const latestComponents = await readEntity("AllUserFilesLimitedProps", {
      personId,
      role: "uploader",
      isArchived: false,
    });
    const componentsNotInCurrentProject = getlatestVersionsByProject(
      latestComponents,
      personId,
      selectedProjectId
    );
    dispatch(finishLoadingAllComponentsUploadedByUser());
    dispatch(
      setComponentsNotInCurrentProject({
        componentsNotInCurrentProject,
      })
    );
  } catch (error) {
    console.error(error);
    dispatch(finishLoadingAllComponentsUploadedByUser({ error }));
  }
};

export const setupState = ({ personId }) => async (dispatch, getState) => {
  const {
    transferManager: {
      selectedProjectId,
      importedComponents,
      uploadedFiles,
      selectedComponentIdToShare,
    },
  } = getState();
  if (selectedProjectId === "Dashboard") {
    // Users can not have add work ideas in dashboard
    if (importedComponents.concat(uploadedFiles).find((c) => c.isIdea)) {
      dispatch(removeIdeasFromDashboard());
    }

    // In dashboard the user shouldn't be able to invite or share
    if (selectedComponentIdToShare) {
      dispatch(stopShareComponent());
    }
    dispatch(stopInviteToProject());
  }

  //In project the Share always defaults to the first file
  if (selectedProjectId !== "Dashboard") {
    const componentId = get(
      uploadedFiles,
      "[0]payload.componentInput.id",
      get(importedComponents, "[0]payload.componentInput.id", "")
    );
    dispatch(setSelectedComponentToShare({ componentId }));
  }
  dispatch(
    getAllProjectsGroupedComponents({ projectId: selectedProjectId, personId })
  );
};

export const getAllProjectsGroupedComponents = ({
  projectId,
  personId,
}) => async (dispatch) => {
  try {
    dispatch(startLoadingComponentsForProject({ projectId }));

    let allProjectsGroupedComponents;
    if (projectId === "Dashboard") {
      allProjectsGroupedComponents = await readEntity(
        "AllDashboardComponents",
        {
          personId,
          role: "uploader",
          componentArchived: false,
          projectArchived: true,
        }
      );
      // quick fix: filter to remove versions (AllDashboardComponents returns duplicates)
      const versions = allProjectsGroupedComponents.reduce((arr, c) => {
        if (c.versions) {
          return [...arr, ...c.versions];
        }
        return arr;
      }, []);

      allProjectsGroupedComponents = allProjectsGroupedComponents.filter(
        (c) => !versions.find((v) => v.id === c.id)
      );
    } else {
      const allProjectsComponents = await readEntity("AllProjectsComponents", {
        projectIds: [projectId],
      });

      allProjectsGroupedComponents = getSameSpaceGroupedComponents(
        projectId,
        allProjectsComponents
      );
    }

    if (allProjectsGroupedComponents) {
      dispatch(
        setAllProjectsGroupedComponents({
          allProjectsGroupedComponents,
        })
      );
      await dispatch(setupSelectableVersionsOptions({ projectId }));
      dispatch(resetVersionsIfFromDifferentProject());
      dispatch(finishLoadingComponentsForProject());
    } else {
      throw new Error(
        "allProjectsGroupedComponents error, returned with: ",
        allProjectsGroupedComponents
      );
    }
  } catch (e) {
    console.error(e);
    dispatch(finishLoadingComponentsForProject({ error: e }));
  }
};

export const changeProject = ({ newProjectId, personId }) => async (
  dispatch,
  getState
) => {
  const { transferManager } = getState();
  if (hasActiveTransferWork(transferManager.uploadedFiles)) return;

  dispatch(setSelectedProject({ newProjectId }));

  dispatch(setupState({ personId }));
};

export const setupSelectableVersionsOptions = ({ projectId }) => async (
  dispatch,
  getState
) => {
  const {
    transferManager: {
      selectedProjectId, // selectedProjectId can be different than projectId passed as prop
      allProjectsGroupedComponents,
    },
    projects: { ids, entities },
  } = getState();

  if (!allProjectsGroupedComponents) {
    throw new Error("No allProjectsGroupedComponents");
  }
  const latestVersionsOfComponents = getLatestVersionsOfComponents(
    allProjectsGroupedComponents
  );

  let selectableVersionsOptions = getSelectableVersionsOptions(
    selectedProjectId,
    latestVersionsOfComponents
  );

  // persist a list of project inside the version dropdown when in Dashboard
  if (selectedProjectId === "Dashboard") {
    const projectNamePlaceholders = ids
      .filter((id) => id !== projectId)
      .map((id) => {
        const { title } = entities[id];
        return {
          type: "None", // TODO: improve
          projectId: id,
          projectName: title,
        };
      });
    // if clicked project does not have any suitable components
    if (
      selectableVersionsOptions.length === 1 &&
      selectableVersionsOptions[0].projectId === "Dashboard" &&
      entities[projectId]
    ) {
      selectableVersionsOptions.push({
        type: "None", // TODO: improve
        projectId,
        projectName: entities[projectId].title,
        value: "No matching components",
      });
    }

    selectableVersionsOptions = [
      ...selectableVersionsOptions,
      ...projectNamePlaceholders,
    ];
  }

  dispatch(
    setSelectableVersionsOptions({
      selectableVersionsOptions,
    })
  );
};
